/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { PrepRun } from "../shared/types";

type Props = { listId: number; date: string; onClose: (changed: boolean) => void };

const fmt = (n: number | null) => (n == null ? "" : String(Number(n.toFixed(2))));

// Today's prep: enter what's on hand, see what to make (par − on hand), tick
// each item off. Every change saves at once.
export const PrepRunner = defineComponent<Props>({
  name: "PrepRunner",
  props: ["listId", "date", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const run = ref<PrepRun | null>(null);
    const changed = ref(false);
    const busy = ref<number | null>(null);

    onMounted(async () => {
      try {
        run.value = (await trpc.workday.prep.openRun.mutate({ prep_list_id: props.listId, date: props.date })) as PrepRun;
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
        props.onClose(false);
      }
    });

    const update = async (id: number, patch: { on_hand?: number | null; to_prep?: number; done?: boolean }) => {
      busy.value = id;
      try {
        run.value = (await trpc.workday.prep.updateItem.mutate({ id, ...patch })) as PrepRun;
        changed.value = true;
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        busy.value = null;
      }
    };

    const progress = computed(() => {
      const items = run.value?.items ?? [];
      return { done: items.filter((i) => i.done).length, total: items.length };
    });

    const numberInput = (value: number | null, onCommit: (v: number | null) => void, cls = "") => (
      <input
        type="text"
        inputmode="decimal"
        class={`input input-bordered input-sm w-20 text-right ${cls}`}
        value={fmt(value)}
        onChange={(e: Event) => {
          const raw = (e.target as HTMLInputElement).value.trim().replace(",", ".");
          const v = raw === "" ? null : Number(raw);
          if (v === null || (Number.isFinite(v) && v >= 0)) onCommit(v);
        }}
      />
    );

    return () => (
      <LemmonModal
        title={run.value?.prep_list_name ?? t("workday.nav.prep")}
        onClose={() => props.onClose(changed.value)}
        renderButtons={() => (
          <div class="border-t p-4 text-right">
            <button class="btn btn-primary" onClick={() => props.onClose(changed.value)}>
              {t("workday.common.done")}
            </button>
          </div>
        )}
      >
        {!run.value ? (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        ) : (
          <div class="space-y-4 p-6">
            <div class="flex items-center gap-3">
              <progress
                class={["progress h-2 flex-1", run.value.completed_at ? "progress-success" : "progress-primary"].join(" ")}
                value={progress.value.done}
                max={progress.value.total || 1}
              />
              <span class="text-sm font-medium tabular-nums">{`${progress.value.done}/${progress.value.total}`}</span>
            </div>
            {run.value.completed_at && (
              <div class="alert alert-success py-2 text-sm">
                <span class="material-symbols-outlined">task_alt</span>
                {t("workday.prep.all_done")}
              </div>
            )}
            <div class="overflow-x-auto">
              <table class="table">
                <thead>
                  <tr>
                    <th class="w-10" />
                    <th>{t("workday.prep.item")}</th>
                    <th class="text-right">{t("workday.prep.par")}</th>
                    <th class="text-right">{t("workday.prep.on_hand")}</th>
                    <th class="text-right">{t("workday.prep.to_prep")}</th>
                  </tr>
                </thead>
                <tbody>
                  {run.value.items.map((i) => (
                    <tr key={i.id} class={i.done ? "bg-success/5" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          class="checkbox checkbox-success"
                          checked={i.done}
                          disabled={busy.value === i.id}
                          onChange={(e: Event) => update(i.id, { done: (e.target as HTMLInputElement).checked })}
                        />
                      </td>
                      <td>
                        <div class={["font-medium", i.done ? "text-base-content/50 line-through" : ""].join(" ")}>{i.name}</div>
                        {i.done && i.done_by_name && <div class="text-[11px] text-base-content/40">{i.done_by_name}</div>}
                      </td>
                      <td class="whitespace-nowrap text-right text-base-content/60">{`${fmt(i.par_qty)} ${i.unit ?? ""}`}</td>
                      <td class="text-right">{numberInput(i.on_hand, (v) => update(i.id, { on_hand: v }))}</td>
                      <td class="whitespace-nowrap text-right">
                        <div class="inline-flex items-center gap-1">
                          {numberInput(i.to_prep, (v) => update(i.id, { to_prep: v ?? 0 }), i.to_prep > 0 && !i.done ? "font-semibold" : "text-base-content/50")}
                          <span class="w-8 text-left text-xs text-base-content/50">{i.unit ?? ""}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </LemmonModal>
    );
  },
});
